'use client'

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Container } from "reactstrap";

import GeneralForm from "components/GeneralForm";
import TitleFormWithEdit from "components/TitleFormWithEdit";
import { validate } from "components/validateGeneralForm";
import { erroInesperado } from "services/Utils";
import perfilService from "services/PerfilService";

export default function FormPerfil() {
  const [data, setData] = useState({ acoes: [] })
  const [edit, setEdit] = useState(false)
  const [tipos, setTipos] = useState([])
  const [acoesElegiveis, setAcoesElegiveis] = useState([])
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const id = parseInt(params.id)
  const novo = (0 === id)
  const clonarDe = parseInt(searchParams.get('clonarDe')) || null
  const isClone = novo && !!clonarDe
  // Tipo so e editavel na criacao do zero - travado na edicao e tambem na clonagem
  // (o clone herda o tipo da origem, ver criterios de aceitacao da SDD 015).
  const tipoTravado = !novo || isClone

  useEffect(() => {
    perfilService.getTipos().then(res => {
      if (res.success) {
        setTipos(res.data)
      }
    })
  }, [])

  // Edicao ou clonagem: carrega o perfil (existente, ou de origem no caso do clone) com suas
  // acoes exatamente como estao gravadas - nunca os "padrao" do tipo, que so valem pra criacao
  // do zero (ver Effect abaixo).
  useEffect(() => {
    const idParaCarregar = novo ? clonarDe : id
    if (!idParaCarregar) {
      setData({ acoes: [] })
      return
    }

    perfilService.getById(idParaCarregar).then(async res => {
      if (!res.success) return

      const origem = res.data
      const acoesRes = await perfilService.getAcoesPorTipo(origem.tipo.id)
      if (acoesRes.success) {
        setAcoesElegiveis(acoesRes.data)
      }

      setData({
        nome: isClone ? `${origem.nome} (cópia)` : origem.nome,
        idTipo: origem.tipo.id,
        acoes: origem.acoes
      })
    })
  }, [id, clonarDe])

  // So se aplica na criacao do zero (nao clonagem): ao trocar o tipo, recarrega as acoes
  // elegiveis daquele tipo e reinicia a selecao pras obrigatorias + padrao.
  useEffect(() => {
    if (!novo || clonarDe)
      return

    if (!data.idTipo) {
      setAcoesElegiveis([])
      return
    }

    perfilService.getAcoesPorTipo(data.idTipo).then(res => {
      if (res.success) {
        setAcoesElegiveis(res.data)
        const iniciais = res.data.filter(el => el.obrigatoria || el.padrao).map(el => el.codigo)
        setData(data => ({ ...data, acoes: iniciais }))
      }
    })
  }, [data.idTipo, novo, clonarDe])

  useEffect(() => {
    if (novo) {
      setEdit(true)
    }
  }, [novo])

  const handleToggleAcao = (codigo, checked) => {
    setData(data => {
      const acoes = new Set(data.acoes || [])
      if (checked) acoes.add(codigo)
      else acoes.delete(codigo)
      return { ...data, acoes: Array.from(acoes) }
    })
  }

  const grupos = useMemo(() => {
    const mapa = {}
    for (const acao of acoesElegiveis) {
      if (!mapa[acao.modulo]) mapa[acao.modulo] = []
      mapa[acao.modulo].push(acao)
    }
    return mapa
  }, [acoesElegiveis])

  const formConfig = useMemo(() => {
    return [
      [
        { name: "nome", label: 'Nome', type: "text", required: true, colspan: 6 },
        { name: "idTipo", label: 'Tipo', type: "combo", options: tipos, optionValue: 'id', optionLabel: 'descricao', showDefault: true, required: true, colspan: 6, disabled: tipoTravado },
        {
          name: "acoes",
          type: "view",
          colspan: 12,
          component: () => (
            <div className="d-flex flex-column" style={{ gap: 20 }}>
              {acoesElegiveis.length === 0 && (
                <div className="text-muted">Selecione um tipo para ver as ações disponíveis.</div>
              )}
              {Object.entries(grupos).map(([modulo, acoesDoModulo]) => (
                <div key={modulo}>
                  <h3>{modulo}</h3>
                  <div className="d-flex flex-column">
                    {acoesDoModulo.map(acao => (
                      <div key={acao.codigo} className="d-flex flex-row align-items-center" style={{ gap: 10, height: 32 }}>
                        <input
                          type="checkbox"
                          id={`acao-${acao.codigo}`}
                          checked={acao.obrigatoria || (data.acoes || []).includes(acao.codigo)}
                          disabled={acao.obrigatoria || !edit}
                          onChange={e => handleToggleAcao(acao.codigo, e.target.checked)}
                        />
                        <label htmlFor={`acao-${acao.codigo}`} className="mb-0">
                          {acao.nome}
                          {acao.obrigatoria && <small className="text-muted"> (obrigatória)</small>}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      ]
    ]
  }, [tipos, tipoTravado, grupos, acoesElegiveis, data.acoes, edit])

  const handleChange = (name, value) => {
    setData({ ...data, [name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = validate(formConfig, data)
    if (errors.length > 0) {
      setData({ ...data, errors })
      return
    }

    try {
      if (isClone) {
        const res = await perfilService.clone(clonarDe, data.nome)
        await perfilService.update(res.data.id, { nome: data.nome, acoes: data.acoes })
      } else if (novo) {
        await perfilService.create({ nome: data.nome, idTipo: data.idTipo, acoes: data.acoes })
      } else {
        await perfilService.update(id, { nome: data.nome, acoes: data.acoes })
      }
      toast('Registro salvo com sucesso!')
      navigate('/admin/perfis')
    } catch (ex) {
      if (ex.response?.data?.message) {
        toast(ex.response.data.message)
        return
      }
      erroInesperado()
    }
  }

  return (
    <Container fluid>
      <TitleFormWithEdit title="Perfis" edit={edit} onChangeEdit={setEdit} showButton={!novo} />
      <Card>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <GeneralForm
              config={formConfig}
              data={data}
              onChange={handleChange}
              disabled={!edit}
            />
            {edit && (
              <Button type="submit" color="primary" className="mt-2">Salvar</Button>
            )}
          </form>
        </CardBody>
      </Card>
    </Container>
  )
}
